import React, {useState} from 'react'

import {useMedia} from 'react-use'
import {palette} from 'styled-tools'
import styled from 'styled-components'
import classNames from 'classnames'
import {
  useRefinementList,
  useClearRefinements,
  useCurrentRefinements,
} from 'react-instantsearch'
import EqualizerLineIcon from 'remixicon-react/EqualizerLineIcon'

import {H5} from '@festi/common/components/typography'
import {gridTheme, styledTheme} from '@festi/common/themes'
import {MainButton} from '@festi/common/components/buttons'
import {CustomDropdown} from '@festi/common/components/form'
import {styleScrollbar} from '@festi/common/styles'

import RangeRefinement from './refinment/RangeRefinment'
import ChoiceRefinement from './refinment/ChoiceRefinement'
import HierarchicalRefinement from './refinment/HierarchicalRefinement'
import {facetNames} from '../utilites/facets'
import {refinementStyle} from '../utilites/refinementStyle'
import {priceRangeAttribute} from '../utilites/refinement'
import {useAlgoliaContext} from '../context/AlgoliaContext'

const Wrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
`

const Text = styled.div`
  white-space: nowrap;
  padding-bottom: 1px;
`

const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const Clear = styled.div`
  color: ${palette('blue')};
  text-decoration: underline;
  cursor: pointer;
`

const CountCircleTransitionWrapper = styled.div`
  display: grid;
  grid-template-columns: 0fr;
  transition: grid-template-columns 200ms ease-out;

  &.show {
    grid-template-columns: 1fr;
  }
`

const OverflowHidden = styled.div`
  overflow: hidden;
`

export const CountCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 500;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: ${palette('blue')};
  background-color: ${styledTheme.palette.ui10Solid};

  &.isFilterBtn {
    opacity: 0;
  }

  &.isFilterBtn.show {
    opacity: 1;
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 280px;

  ${refinementStyle}

  &.overflow {
    max-height: 400px;
    overflow-y: auto;

    ${styleScrollbar}
  }
`

const ClearBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  padding-block: 0;
  padding-inline: 16px;
  color: ${styledTheme.palette.ui90Solid};
  border: 0;
  background: transparent;
  cursor: pointer;
`
const ClearBtnText = styled.span`
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 1.5px;
`

export function ClearButton() {
  const {items} = useCurrentRefinements()
  const {refine} = useClearRefinements()

  const hasRefinements = items?.length

  if (!hasRefinements) return null

  return (
    <ClearBtn onClick={() => refine()}>
      <ClearBtnText>Hreinsa</ClearBtnText>
    </ClearBtn>
  )
}

export function AllFiltersButton() {
  const {items} = useCurrentRefinements()
  const {setOpenRefine} = useAlgoliaContext()

  const numberOfRefinements = items?.reduce(
    (acc, item) => acc + (item.refinements ? item.refinements.length : 1),
    0,
  )

  return (
    <div>
      <MainButton
        label={
          <Flex>
            <Text>Allar síur</Text>
            <CountCircleTransitionWrapper
              className={classNames({
                show: !!numberOfRefinements,
              })}
            >
              <OverflowHidden>
                <CountCircle
                  className={classNames({
                    isFilterBtn: true,
                    show: !!numberOfRefinements,
                  })}
                >
                  {numberOfRefinements}
                </CountCircle>
              </OverflowHidden>
            </CountCircleTransitionWrapper>
          </Flex>
        }
        style={{
          border: `1px solid ${
            !!numberOfRefinements
              ? styledTheme.palette.ui50Solid
              : 'transparent'
          }`,
        }}
        prefixIcon={EqualizerLineIcon}
        iconColor={styledTheme.palette.ui20Solid}
        buttonVariant="negative"
        onClick={() => setOpenRefine(true)}
      />
    </div>
  )
}

interface PriceRangeProps {
  displayNone?: boolean
}

export function PriceRangeAttribute({displayNone}: PriceRangeProps) {
  const {items} = useRefinementList({
    attribute: priceRangeAttribute,
    limit: 200,
  })
  const {refine: clearRefinements} = useClearRefinements({
    includedAttributes: [priceRangeAttribute],
  })

  const [hasRefinement, setHasRefinement] = useState(0)

  const label = 'Verð'

  const buttonLabel = <Text>{label}</Text>

  return (
    <CustomDropdown
      label={buttonLabel}
      active={!!hasRefinement}
      content={
        <>
          <Header>
            <H5>{label}</H5>

            <Flex>
              <Clear onClick={() => clearRefinements()}>Hreinsa</Clear>
              {!!hasRefinement && <CountCircle>{hasRefinement}</CountCircle>}
            </Flex>
          </Header>

          <List>
            <RangeRefinement
              items={items}
              onChange={(value: number) => setHasRefinement(value)}
            />
          </List>
        </>
      }
      displayNone={displayNone || items?.length === 0}
    />
  )
}

interface CategoryProps {
  displayNone?: boolean
}

export function CategoryAttribute({displayNone}: CategoryProps) {
  const attribute = 'categories'
  const {items, refine} = useRefinementList({
    attribute,
    limit: 200,
  })
  const {refine: clearRefinements} = useClearRefinements({
    includedAttributes: [attribute],
  })

  const label = 'Flokkar'
  const numberRefined = items?.filter((i) => i.isRefined)?.length

  const buttonLabel = <Text>{label}</Text>

  return (
    <CustomDropdown
      label={buttonLabel}
      active={!!numberRefined}
      content={
        <>
          <Header>
            <H5>{label}</H5>

            <Flex>
              <Clear onClick={() => clearRefinements()}>Hreinsa</Clear>
              {!!numberRefined && <CountCircle>{numberRefined}</CountCircle>}
            </Flex>
          </Header>

          <List>
            <HierarchicalRefinement
              items={items}
              attribute={attribute}
              refine={refine}
            />
          </List>
        </>
      }
      displayNone={displayNone || items?.length === 0}
    />
  )
}

interface ItemProps {
  label: string
  attribute: string
  displayNone?: boolean
}

export function RefinementItem({label, attribute, displayNone}: ItemProps) {
  const {items, refine} = useRefinementList({
    attribute: attribute,
    limit: 200,
  })
  const {refine: clearRefine} = useClearRefinements({
    includedAttributes: [attribute],
  })

  const numberRefined = items?.filter((i) => i.isRefined)?.length
  const overflow = items.length > 18

  const buttonLabel = <Text>{label}</Text>

  return (
    <CustomDropdown
      label={buttonLabel}
      active={!!numberRefined}
      content={
        <>
          <Header>
            <H5>{label}</H5>

            <Flex>
              <Clear onClick={() => clearRefine()}>Hreinsa</Clear>
              {!!numberRefined && <CountCircle>{numberRefined}</CountCircle>}
            </Flex>
          </Header>

          <List className={classNames({overflow})}>
            <ChoiceRefinement items={items} refine={refine} />
          </List>
        </>
      }
      displayNone={
        displayNone || items?.length === 0 || attribute === 'categories'
      }
    />
  )
}

interface Props {
  facets: string[]
  attrNameMap: Record<string, string>
}

export default function CustomRefinement({facets, attrNameMap}: Props) {
  const {initialQuery} = useAlgoliaContext()

  const isMobile = useMedia(`(max-width: ${gridTheme.breakpoints.md}px)`, false)
  const isSmallMobile = useMedia(
    `(max-width: ${gridTheme.breakpoints.sm}px)`,
    false,
  )

  return (
    <Wrapper>
      <AllFiltersButton />

      <PriceRangeAttribute displayNone={isSmallMobile} />

      {!!initialQuery && <CategoryAttribute displayNone={isSmallMobile} />}

      {facets?.map((attribute: string, index) => (
        <RefinementItem
          key={attribute}
          label={facetNames(attrNameMap)?.[attribute] || attribute}
          attribute={attribute}
          displayNone={isSmallMobile || index > (isMobile ? 3 : 6)}
        />
      ))}

      <ClearButton />
    </Wrapper>
  )
}
